<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import PersonalPieChart from '@/components/saju-insight/personality/PersonalPieChart.vue';
import {
  parseKeywords,
  insertLineBreak,
  transformMarkedText,
  splitBySentence,
} from '@/composables/splitSentence';
const props = defineProps({
  yData: Object,
});

const yinyangClass = computed(() => {
  if (yinyangChartData.value?.startsWith('+')) return 'st-yang';
  return 'st-yin';
});
const johooClass = computed(() => {
  if (jhChartData.value?.startsWith('+')) return 'st-jo';
  return 'st-hoo';
});

// 음양분석
const yinyangStatus = computed(() => props.yData?.분석상세?.음양조후?.음양상태);
const yinyangChartData = computed(() => props.yData?.분석상세?.음양조후?.음양비율);
const yinyangDisplay = computed(() => {
  const val = props.yData?.분석상세?.음양조후?.음양비율;
  return typeof val === 'string' ? val.replace(/^[-+]/, '') : val;
});
const yinyangDesc = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.음양해석?.개념특징?.개념 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const yinyangAttribute = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.음양해석?.개념특징?.특징 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const yinyangStKeywords = computed(() => {
  const raw = props.yData?.분석상세?.음양해석?.개념특징?.강점요약;
  return raw ? parseKeywords(raw) : [];
});
const yinyangWeaKeywords = computed(() => {
  const raw = props.yData?.분석상세?.음양해석?.개념특징?.약점요약;
  return raw ? parseKeywords(raw) : [];
});
// 음양 차트 데이터
const parsedYinYang = computed(() => {
  const value = parseFloat(yinyangChartData.value?.replace('+', '').replace('-', '')) || 0;
  let yang = 0;
  let yin = 0;

  if (yinyangChartData.value?.startsWith('+')) {
    yang = value;
    yin = 100 - yang;
    // yinyangClass.value = 'st-yang'
  } else if (yinyangChartData.value?.startsWith('-')) {
    yin = value;
    yang = 100 - yin;
    // yinyangClass.value = 'st-yin'
  } else {
    yang = value;
    yin = 100 - yang;
  }
  return { yang, yin };
});
const yinYangChartData = computed(() => ({
  '음의 기운': parsedYinYang.value.yin.toFixed(1),
  '양의 기운': parsedYinYang.value.yang.toFixed(1),
}));
const yinYangLabelList = ['음의 기운', '양의 기운'];
const yinYangColorMap = {
  '양의 기운': '#EAEAEA',
  '음의 기운': '#727272',
};

// 조후분석
const jhStatus = computed(() => props.yData?.분석상세?.음양조후?.조후상태);
const jhChartData = computed(() => props.yData?.분석상세?.음양조후?.조후비율);
const jhDisplay = computed(() => {
  const val = props.yData?.분석상세?.음양조후?.조후비율;
  return typeof val === 'string' ? val.replace(/^[-+]/, '') : val;
});
const jhDesc = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.조후해석?.개념특징?.개념 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const jhAttribute = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.조후해석?.개념특징?.특징 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const jhStKeywords = computed(() => {
  const raw = props.yData?.분석상세?.조후해석?.개념특징?.강점요약;
  return raw ? parseKeywords(raw) : [];
});
const jhWeaKeywords = computed(() => {
  const raw = props.yData?.분석상세?.조후해석?.개념특징?.약점요약;
  return raw ? parseKeywords(raw) : [];
});
// 조후 차트 데이터
const parsedJohoo = computed(() => {
  const value = parseFloat(jhChartData.value?.replace('+', '').replace('-', '')) || 0;
  let hot = 0;
  let cold = 0;
  if (jhChartData.value?.startsWith('+')) {
    hot = value;
    cold = 100 - hot;
    // johooClass.value = 'st-jo'
  } else if (jhChartData.value?.startsWith('-')) {
    cold = value;
    hot = 100 - cold;
  } else {
    hot = value;
    cold = 100 - hot;
    // johooClass.value = 'st-hoo'
  }
  return { hot, cold };
});
const johooChartData = computed(() => ({
  '뜨거운 기운': parsedJohoo.value.hot.toFixed(1),
  '차가운 기운': parsedJohoo.value.cold.toFixed(1),
}));
const johooLabelList = ['뜨거운 기운', '차가운 기운'];
const johooColorMap = {
  '뜨거운 기운': '#FFD374',
  '차가운 기운': '#75A4FD',
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">4</span>
          <span class="detail-tit__txt yongsin">음양분석</span>
        </div>
        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="person-content-box">
            <div class="ys-title-box">
              <div class="ys-title-box__title">{{ yinyangStatus }}</div>
              <div class="ys-title-box__desc" :class="yinyangClass">{{ yinyangDisplay }}</div>
            </div>
          </div>
          <div class="ys-img-0301-box">
            <PersonalPieChart
              :chartData="yinYangChartData"
              :labelList="yinYangLabelList"
              :colorMap="yinYangColorMap"
              centerTitle="음/양"
              labelDirection="column"
              :isMobile="isMobile"
            />
          </div>
          <div class="yongsin-desc-box">
            <div v-for="(html, idx) in yinyangDesc" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">특징</div>
            <div v-for="(html, idx) in yinyangAttribute" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="ys-word-wrap">
            <div class="ys-word-box st-blue">
              <div class="tit-box">강점</div>
              <div class="word-box">
                <span v-for="(word, idx) in yinyangStKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
            <div class="ys-word-box st-pink">
              <div class="tit-box">보완</div>
              <div class="word-box">
                <span v-for="(word, idx) in yinyangWeaKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">5</span>
          <span class="detail-tit__txt yongsin">조후분석</span>
        </div>

        <div class="personality-wrapper content_info_area_box">
          <div class="person-content-box">
            <div class="ys-title-box">
              <div class="ys-title-box__title">{{ jhStatus }}</div>
              <div class="ys-title-box__desc" :class="johooClass">{{ jhDisplay }}</div>
            </div>
          </div>

          <div class="ys-img-0302-box">
            <PersonalPieChart
              :chartData="johooChartData"
              :labelList="johooLabelList"
              :colorMap="johooColorMap"
              centerTitle="조후"
              labelDirection="column"
              :isMobile="isMobile"
            />
          </div>
          <div class="yongsin-desc-box">
            <div v-for="(html, idx) in jhDesc" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">특징</div>
            <div v-for="(html, idx) in jhAttribute" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="ys-word-wrap">
            <div class="ys-word-box st-blue">
              <div class="tit-box">강점</div>
              <div class="word-box">
                <span v-for="(word, idx) in jhStKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
            <div class="ys-word-box st-pink">
              <div class="tit-box">보완</div>
              <div class="word-box">
                <span v-for="(word, idx) in jhWeaKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
</style>
